/**
 * All Honors, Mixed Terminals, and Pure Terminals — pure tile-kind scans over
 * the whole hand (concealed tiles + every meld's tiles + the winning tile),
 * no decomposition needed, same style as `allSimples.ts`/`flush.ts`.
 *
 * Mixed Terminals (every tile is a terminal OR an honor, "Hun Yao Jiu") and
 * Pure Terminals (every tile is a terminal, zero honors, zero simples) are
 * mutually inclusive-by-condition — a Pure Terminals hand also satisfies
 * Mixed Terminals' looser "no simples" condition — so `matchTerminals`
 * follows `flush.ts`'s Half/Full Flush precedent: when the stricter Pure
 * Terminals condition holds, it scores INSTEAD of Mixed Terminals, not in
 * addition to it.
 *
 * An All-Honors or an all-terminal/honor hand is also inherently an
 * All-Triplets hand (honors and terminal-only shapes can't form runs) —
 * that's independent-detector doubles-stacking, not something to
 * special-case here.
 *
 * `ALL_HONORS_DOUBLES` matches the sourced sloperama.com value used in
 * `basePoints.ts`/`flush.ts` (3 doubles, same tier as Full Flush).
 * `MIXED_TERMINALS_DOUBLES` and `PURE_TERMINALS_DOUBLES` are estimated,
 * same honesty as `sevenPairs.ts` — the source table didn't give explicit
 * numbers for these two. 1 and 3 are chosen for internal consistency with
 * the rest of this table (1 = a modest common double, matching All
 * Triplets/All Simples/Half Flush's tier; 3 = the "narrow, hard-to-make"
 * tier matching Full Flush/All Honors). Not asserted as tournament-exact.
 */

import type { Tile } from '../../game/types.js';
import type { MatchedPattern, ScoringContext } from '../types.js';

const ALL_HONORS_DOUBLES = 3;
const MIXED_TERMINALS_DOUBLES = 1;
const PURE_TERMINALS_DOUBLES = 3;

const TERMINAL_RANKS = [1, 9];

function allHandTiles(context: ScoringContext): Tile[] {
  return [...context.concealedTiles, ...context.melds.flatMap((meldEntry) => meldEntry.tiles), context.winningTile];
}

function isTerminal(tile: Tile): boolean {
  return tile.kind === 'suit' && TERMINAL_RANKS.includes(tile.rank);
}

export function matchAllHonors(context: ScoringContext): MatchedPattern | null {
  const tiles = allHandTiles(context);
  const everyHonor = tiles.every((tile) => tile.kind === 'honor');
  return everyHonor ? { name: 'allHonors', kind: 'double', value: ALL_HONORS_DOUBLES } : null;
}

export function matchTerminals(context: ScoringContext): MatchedPattern | null {
  const tiles = allHandTiles(context);
  const everyTerminalOrHonor = tiles.every((tile) => tile.kind === 'honor' || isTerminal(tile));
  if (!everyTerminalOrHonor) return null;

  const hasHonor = tiles.some((tile) => tile.kind === 'honor');
  if (!hasHonor) return { name: 'pureTerminals', kind: 'double', value: PURE_TERMINALS_DOUBLES };
  return { name: 'mixedTerminals', kind: 'double', value: MIXED_TERMINALS_DOUBLES };
}
